import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { ProjectDoc, SubmittalPackage } from 'common';
import { User } from 'src/global/models';
import { ProjectService } from 'src/projects/services/ProjectService';

@Component({
  tag: 'submittal-package-selector',
})
export class SubmittalPackageSelector {
  /**
   * Resolves with the confirmed package, `null` if cleared, or `undefined` if dismissed.
   */
  static async present(projectId: string, selectedPackage?: SubmittalPackage): Promise<SubmittalPackage | null | undefined> {
    const modal = await modalController.create({
      component: 'submittal-package-selector',
      componentProps: { projectId, selectedPackage },
      backdropDismiss: false,
      cssClass: 'modal-height-auto',
    });
    return new Promise(async resolve => {
      modal.addEventListener('selected', (e: CustomEvent<SubmittalPackage | null>) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(undefined);
      });
      await modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() selectedPackage?: SubmittalPackage;

  @State() packages: SubmittalPackage[] = [];
  @State() currentSelection: SubmittalPackage | null = null;
  @State() isAdding: boolean = false;
  @State() newName: string = '';
  @State() isSaving: boolean = false;

  @Event() selected: EventEmitter<SubmittalPackage | null>;
  @Event() dismiss: EventEmitter<void>;

  private project: ProjectDoc;

  get user(): User {
    return globalThis.user;
  }

  get canWrite(): boolean {
    return this.user.getSubmittalsPermissions(this.projectId).canWrite;
  }

  async componentWillLoad() {
    this.currentSelection = this.selectedPackage || null;

    const db = ProjectService.getProjectDb(this.projectId);
    const doc: ProjectDoc = await db.getDoc(this.projectId);
    if (doc) {
      this.handleProjectDoc(doc);
    } else {
      db.addListener('complete', docs => this.handleProjectDoc(docs[0] as ProjectDoc));
    }
  }

  private handleProjectDoc(doc: ProjectDoc) {
    this.project = doc;
    this.packages = doc.submittals_packages || [];
  }

  private togglePackage(pkg: SubmittalPackage) {
    this.currentSelection = this.currentSelection?.title === pkg.title ? null : pkg;
  }

  private enterAddMode() {
    this.isAdding = true;
    this.newName = '';
  }

  private exitAddMode() {
    this.isAdding = false;
    this.newName = '';
  }

  private handleConfirm() {
    this.selected.emit(this.currentSelection);
  }

  private async handleAdd() {
    const name = this.newName.trim();
    if (!name || this.isSaving) return;

    const existing = this.packages.find(pkg => pkg.title.toLowerCase() === name.toLowerCase());
    if (existing) {
      this.selected.emit(existing);
      return;
    }

    this.isSaving = true;
    try {
      const newPackage: SubmittalPackage = { title: name };
      this.project.submittals_packages = this.project.submittals_packages || [];
      this.project.submittals_packages.push(newPackage);
      this.project = await ProjectService.saveProjectDoc(this.project);
      this.packages = this.project.submittals_packages;
      this.selected.emit(newPackage);
    } finally {
      this.isSaving = false;
    }
  }

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={this.isAdding ? 'Add Package' : 'Select Package'}
          showBack={this.isAdding}
          onBack={() => this.exitAddMode()}
          onDismiss={() => this.dismiss.emit()}
        />
        {this.isAdding ? (
          <div class="modal-content cc-padding">
            <ion-input
              label="Package name"
              label-placement="stacked"
              placeholder="Enter package name"
              value={this.newName}
              autofocus
              onIonInput={e => (this.newName = (e.target as HTMLIonInputElement).value as string)}
            />
          </div>
        ) : (
          <div class="modal-content cc-scrollbars">
            <ion-list class="cc-no-line-list transparent-background" style={{ marginBottom: '30px' }}>
              {this.packages.length === 0 ? (
                <ion-text class="ion-padding-horizontal">No packages available.</ion-text>
              ) : (
                this.packages.map(pkg => (
                  <selector-item selected={this.currentSelection?.title === pkg.title} showCheckmark={true} onItemClick={() => this.togglePackage(pkg)}>
                    {pkg.title}
                  </selector-item>
                ))
              )}
            </ion-list>
          </div>
        )}
        <ion-footer>
          <ion-toolbar>
            {!this.isAdding && this.canWrite && (
              <ion-button slot="start" shape="round" fill="outline" onClick={() => this.enterAddMode()}>
                Add package
              </ion-button>
            )}
            {this.isAdding ? (
              <ion-button slot="end" shape="round" color="primary" fill="solid" disabled={!this.newName.trim() || this.isSaving} onClick={() => this.handleAdd()}>
                Add {this.newName.trim()}
              </ion-button>
            ) : (
              <ion-button slot="end" shape="round" color="primary" fill="solid" onClick={() => this.handleConfirm()}>
                {this.currentSelection ? `Select ${this.currentSelection.title}` : 'Select None'}
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
